import { Dispatch, SetStateAction, SVGProps } from 'react';
import { AdmissionAddon } from '@domain/addon';
import { Admission, AdmissionType, Status, Translations } from '@domain/admission';
import { Channel } from '@domain/channel';
import { Event } from '@domain/event';
import { FormFieldPresets } from '@domain/form-field-presets';
import { CreateAdmissionRequest } from '@hooks/admissions/type';
import { GalleryImagesController } from '@hooks/gallery/use-gallery-images';
import moment from 'moment-timezone';

export interface Props {
  handleClose: () => void;
  event: Event;
  handleSubmit: (ticket: CreateAdmissionRequest) => void;
  icon: (props: SVGProps<SVGSVGElement>) => JSX.Element;
  iconClassName: string;
  formFieldsPresets: FormFieldPresets;
  type: AdmissionType;
  offer?: Admission | null;
  afterOfferDescription?: string;
  handleDelete?: (admission: Admission, type: AdmissionType) => void;
  isCreatedFromPreset?: boolean;
  handleSaveConfirmModal: () => void;
  handleCloseConfirmModal: () => void;
  isConfirmModalOpen?: boolean;
  setActiveConfirmModal?: Dispatch<SetStateAction<boolean>>;
  isDuplicatedAdmission?: boolean;
  dataTestid?: string;
  ddpPreset?: number;
  /** Gallery controller threaded by `useAdmissionSave`. See the modal-level prop for details. */
  imagesController?: GalleryImagesController;
}

export interface FormField {
  name: string;
  slug: string | null;
  dataType: string;
  isRequired: boolean;
  min?: number;
  max?: number;
  disabled: boolean;
}

export const SalesStartStatusNow = 'now';
export const SalesStartStatusCustomDate = 'customDate';
export const SalesStartStatusAfterOffer = 'afterOffer';

export const SalesEndStatusEventsStart = 'atEventsStart';
export const SalesEndStatusEventsEnd = 'atEventsEnd';
export const SalesEndStatusCustomDate = 'customDate';

export type SalesStartDate =
  | typeof SalesStartStatusAfterOffer
  | typeof SalesStartStatusCustomDate
  | typeof SalesStartStatusNow;
export type SalesEndDate =
  | typeof SalesEndStatusCustomDate
  | typeof SalesEndStatusEventsEnd
  | typeof SalesEndStatusEventsStart;

export interface Form {
  price: string;
  prevStatus: Status;
  status: Status;
  name: Translations;
  quantity: string;
  guestCapacity: string;
  itemsPerTransaction: { min: string; max: string };
  salesStartType: SalesStartDate;
  salesEndType: SalesEndDate;
  salesStartDate: moment.Moment | null;
  salesStartTime: string;
  salesEndDate: moment.Moment | null;
  salesEndTime: string;
  salesStartAfterOffer: string | null;
  description: Translations;
  descriptionLanguage: string;
  accessCode: string | null;
  isPrivate: boolean;
  isForAllChannels: boolean;
  isNominative: boolean;
  bookingFormFields: FormField[];
  ticketHolderFormFields: FormField[];
  salesQuota?: string;
  limitAllChannels?: string;
  channels: Channel[];
  isChannelsDirty: boolean;
  isAddonsDirty: boolean;
  color: string | null;
  ddp: string;
  isSiaeExcluded?: boolean;
  drinksIncluded: string;
  accessValidity: string;
  validFrom: moment.Moment | null;
  validFromTime: string;
  validUntil: moment.Moment | null;
  validUntilTime: string;
  acceptDownPayment: boolean;
  onlinePrice: string;
  hasMandatoryAddOns: boolean;
  addOnChoiceMode: boolean;
  vatRate: string;
  ticketsLeftThreshold: string;
}

export interface DefaultValuesProps {
  locale: string;
  event: Event;
  offer?: Admission | null;
  formFieldsPresets: FormFieldPresets;
  type: string;
  ddpPreset?: number;
  now: moment.Moment;
  shouldPrecheckTaxIdPt?: boolean;
}

export interface GetTicketFromFormProps {
  form: Partial<Form>;
  isCreatedFromPreset: boolean;
  eventId: string;
  type: AdmissionType;
  currency: string;
  salesQuota: string;
  admissionChannels?: {
    checked: boolean;
    value: string;
    id: string;
    name: string;
    slug: string;
  }[];
  admissionAddons?: AdmissionAddon[];
  timezone: string;
  isValiditySectionVisible: string;
  offer?: Admission | null;
  salesEndType: SalesEndDate | '';
  eventStartingTime: number;
  eventEndingTime: number;
  salesStartType: SalesStartDate | '';
  selectedOfferTriggerId?: string;
  salesStartTime?: string;
  salesStartDate?: moment.Moment;
  defaultTicketsLeftThreshold: number;
}
